




















































































































































import { Component, Mixins, Watch, Prop } from "vue-property-decorator";
import { IAccount } from "@/models/accounts";
import { ICurrency } from "@/models/constants";
import CurrenciesStore from "@/mixins/constants/currenciesStore";

import { DataModules } from "@/store/modules/data";
import { IFilter, ISorter } from "@/models/tables";
import RequestPromises from "@/mixins/requestPromises";
import { indexColor } from "@/components/app/global/stats/charts/utils";
import { IImport } from "@/models/imports";

@Component
export default class MRRTable extends Mixins(RequestPromises, CurrenciesStore) {
  // Props
  @Prop({ required: true })
  public dateFrom!: Date;

  @Prop({ required: true })
  public currencyCode!: ICurrency["code"];

  @Prop({ default: null })
  public readonly importId!: IImport["id"];

  @Prop({ default: null })
  public accountId!: IAccount["id"];

  // Data
  public sorters: ISorter[] = [];
  public filters: IFilter[] = [];
  public dataModule: string = DataModules.STATS;
  public isLoading = true;
  public requestError = false;
  public scope = "mrr";
  public hover = { index: undefined, column: undefined };

  private debounceLoadData = this.$_.debounce(() => {
    this.loadData();
  }, 500);

  // Lifecycle methods
  public async created() {
    this.generateChartColors();
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.STATS,
      scope: this.innerScope,
      vm: this
    });
  }

  // Watch
  @Watch("dateFrom")
  public onParamsChagned(newVal, oldval) {
    if (!this.$_.isEqual(newVal, oldval)) this.debounceLoadData();
  }

  @Watch("currencyCode")
  public onCurrencyCodeChagned(newVal, oldval) {
    if (!this.$_.isEqual(newVal, oldval)) {
      this.isLoading = true;
      this.debounceLoadData();
    }
  }

  // Getters
  public get groupedTableData() {
    if (this.isLoading) return [Array(12)];
    return this.$_.groupBy(this.table, i =>
      this.$moment(this.dateFrom).isBefore(i.date)
    );
  }

  public get currency() {
    return this.$_.find(this.currencies, c => c.code === this.currencyCode);
  }

  public get innerScope() {
    return this.accountId ? `${this.scope}${this.accountId}` : this.scope;
  }

  public get table() {
    if (!this.statResult?.result || !this.currencyCode) return [];

    let data = this.statResult.result;

    let index = 0;
    data = this.$_.map(data, i => ({
      ...i[this.currencyCode],
      index: index++,
      mDate: i?.[this.currencyCode]
        ? this.$moment(i[this.currencyCode].date)
        : null
    })).filter(i => !!i);

    return this.$_.orderBy(
      data,
      i => (i.mDate ? i.mDate.toDate() : i.date),
      "asc"
    );
  }

  public get statResult(): any {
    return this.$store.getters["data/single"]({
      storeModule: DataModules.STATS,
      scope: this.innerScope
    });
  }

  // Methods
  public cellClass(tick, columnIndex) {
    return {
      [`is-hovered-${columnIndex}`]:
        this.hover?.index === tick.index && this.hover?.column === columnIndex
    };
  }

  public generateChartColors() {
    for (let index = 0; index < 5; index++) {
      const color = indexColor({ index });
      document.documentElement.style.setProperty(
        `--mrr-index-color-${index}`,
        ` ${color.borderColor}`
      );
    }
  }

  public isSelectedDate(date) {
    return this.$moment(this.dateFrom).isSame(date, "day");
  }

  public onHover(hover) {
    this.hover = hover;
    this.$emit("hover", hover);
  }

  public async loadData() {
    try {
      this.isLoading = true;
      await this.$store.dispatch("constants/getCurrencies");
      await this.getData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
    }
  }

  public getData() {
    const params = {
      type: "mrr",
      date_from: this.$moment(this.dateFrom).format(`YYYY-MM-DD`),
      ...(this.$_.isNil(this.importId) ? {} : { mrr_import_id: this.importId })
    };

    return this.$store.dispatch(`data/${DataModules.STATS}/get`, {
      vm: this,
      scope: this.innerScope,
      params: this.accountId
        ? {
            ...params,
            account_id: this.accountId,
            currency_code: this.currencyCode
          }
        : { ...params, currency_code: this.currencyCode }
    });
  }
}
